import {
  type BridgeOperation,
  bridgeInputSchemas,
  type ElementSelection,
  type PageHeader,
  type PageSection,
  type ViewContext,
} from '@crowdex/plugin-contracts';

/** What the page holding a view does for it; each handler validates nothing, the host does. */
export type AppBridgeHandlers = {
  context(): ViewContext;
  /** Asks the agent: the open conversation, or a new one. */
  ask(text: string): void | Promise<void>;
  /** Puts elements the user commented on in the composer; `send` sends them. */
  attach(selections: ElementSelection[], send: boolean): void | Promise<void>;
  /** Opens a route or an artifact; a dialog resolves with what it closed with. */
  open(
    target:
      | {
          path: string;
          in?: 'main' | 'side' | 'dialog' | 'window';
          input?: unknown;
          size?: 'default' | 'wide';
        }
      | { artifact: { space: string; path: string }; in?: 'main' | 'side' | 'dialog' | 'window' }
      | { conversation: string }
      | { connect: string; connection?: string },
  ): unknown | Promise<unknown>;
  closeDialog(result: unknown): void;
  confirm(input: {
    title: string;
    message?: string;
    confirm?: string;
    destructive?: boolean;
  }): boolean | Promise<boolean>;
  /** The view shows `item` of its sidebar `section` (`page.sidebars`; the first when left out). */
  selectItem(item: string | null, section?: string): void;
  refreshPage(): void;
  /** The live sections of what the view shows (`page.sections.set`); a view in no tab has none. */
  setSections?(sections: PageSection[]): void;
  /** The host's bar over the view (`page.header.set`); a host without a bar ignores it. */
  setHeader?(header: PageHeader): void;
  /** Goes to a route of the view (`page.navigate`): a new level unless `push` is false. */
  navigate?(route: string, push: boolean): void;
  /** Goes up a level (`page.back`). */
  back?(): void;
  /** Shows a route of the view in the frame's inspector (`page.inspector.open`); none ignores it. */
  openInspector?(route: string, title: string | undefined): void;
  /** Closes the inspector (`page.inspector.close`). */
  closeInspector?(): void;
};

const MAX_MESSAGE_BYTES = 2 * 1024 * 1024;

/** The context as plain data: renderer state may hold proxies or functions a message cannot carry. */
function plain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
const MAX_IN_FLIGHT = 32;

/** Runs one bridge operation after validating its input. */
export async function runBridgeOperation(
  handlers: AppBridgeHandlers,
  operation: string,
  input: unknown,
): Promise<unknown> {
  if (!(operation in bridgeInputSchemas)) throw new Error(`Unknown operation ${operation}`);
  const schema = bridgeInputSchemas[operation as keyof typeof bridgeInputSchemas];
  const parsed = schema.safeParse(input);
  if (!parsed.success) throw new Error(`Invalid input for ${operation}`);
  const value = parsed.data as never;
  switch (operation as BridgeOperation) {
    case 'context.get':
      return handlers.context();
    case 'agent.ask':
      await handlers.ask((value as { text: string }).text);
      return null;
    case 'agent.attach': {
      const { selections, send } = value as { selections: ElementSelection[]; send?: boolean };
      await handlers.attach(selections, send === true);
      return null;
    }
    case 'view.open':
      return (await handlers.open(value)) ?? null;
    case 'dialog.close':
      handlers.closeDialog((value as { result?: unknown } | undefined)?.result);
      return null;
    case 'host.confirm':
      return handlers.confirm(value);
    case 'page.select': {
      const { item, section } = value as { item: string | null; section?: string };
      if (section === undefined) handlers.selectItem(item);
      else handlers.selectItem(item, section);
      return null;
    }
    case 'page.refresh':
      handlers.refreshPage();
      return null;
    case 'page.sections.set':
      handlers.setSections?.((value as { sections: PageSection[] }).sections);
      return null;
    case 'page.header.set':
      handlers.setHeader?.(value as PageHeader);
      return null;
    case 'page.navigate': {
      const { route, push } = value as { route: string; push?: boolean };
      handlers.navigate?.(route, push !== false);
      return null;
    }
    case 'page.back':
      handlers.back?.();
      return null;
    case 'page.inspector.open': {
      const { route, title } = value as { route: string; title?: string };
      handlers.openInspector?.(route, title);
      return null;
    }
    case 'page.inspector.close':
      handlers.closeInspector?.();
      return null;
    default:
      throw new Error(`Unsupported operation ${operation}`);
  }
}

/**
 * Serves the bridge of the view in `frame` (loaded from `origin`): answers its hello with a
 * message port and its context, then runs its operations. Returns a function pushing a new
 * context and one disposing the bridge.
 */
export function hostAppBridge(
  frame: HTMLIFrameElement,
  origin: string,
  handlers: AppBridgeHandlers,
): { pushContext(): void; dispose(): void } {
  let port: MessagePort | null = null;
  let session: string | null = null;
  let inFlight = 0;
  const onMessage = (event: MessageEvent) => {
    if (event.source !== frame.contentWindow || event.origin !== origin) return;
    const hello = event.data as { type?: unknown; session?: unknown } | null;
    if (hello?.type !== 'crowdex-app:hello' || typeof hello.session !== 'string') return;
    // A view repeats its hello until answered: answer each document once.
    if (hello.session === session) return;
    session = hello.session;
    port?.close();
    const channel = new MessageChannel();
    port = channel.port1;
    const current = port;
    current.onmessage = async ({ data }) => {
      const request = data as {
        type?: unknown;
        id?: unknown;
        operation?: unknown;
        input?: unknown;
      };
      if (request?.type !== 'request' || typeof request.id !== 'number') return;
      const reply = (message: object) =>
        current.postMessage({ type: 'response', id: request.id, ...message });
      if (typeof request.operation !== 'string') return reply({ error: 'Invalid request' });
      if (inFlight >= MAX_IN_FLIGHT) return reply({ error: 'Too many requests' });
      try {
        if (JSON.stringify(request.input ?? null).length > MAX_MESSAGE_BYTES)
          return reply({ error: 'Request too large' });
      } catch {
        return reply({ error: 'Invalid input' });
      }
      inFlight += 1;
      try {
        reply({ result: await runBridgeOperation(handlers, request.operation, request.input) });
      } catch (error) {
        reply({ error: error instanceof Error ? error.message : String(error) });
      } finally {
        inFlight -= 1;
      }
    };
    try {
      frame.contentWindow?.postMessage(
        { type: 'crowdex-app:init', session, context: plain(handlers.context()) },
        origin,
        [channel.port2],
      );
    } catch (error) {
      console.error('[apps] could not start the view bridge', error);
    }
  };
  window.addEventListener('message', onMessage);
  return {
    pushContext() {
      port?.postMessage({ type: 'context', context: plain(handlers.context()) });
    },
    dispose() {
      window.removeEventListener('message', onMessage);
      port?.close();
      port = null;
    },
  };
}
